import { useState, useEffect, useContext, useMemo, useRef } from 'react'
import { AuthContext } from '../../contexts/auth'
import { changePassword } from '../../api/auth'
import { useTranslation } from 'react-i18next'
import { AppLayout } from '../../components/AppLayout'
import { Card } from '../../components/ui/card'
import { Button } from '../../components/ui/button'
import { Input } from '../../components/ui/input'
import { Label } from '../../components/ui/label'
import { Avatar, AvatarFallback, AvatarImage } from '../../components/ui/avatar'
import Cropper from 'react-easy-crop'
import 'react-easy-crop/react-easy-crop.css'
import getCroppedImg from '../../Utils/cropImage'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger
} from '../../components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger
} from '../../components/ui/dropdown-menu'
import {
  User,
  Mail,
  Briefcase,
  Calendar,
  Save,
  Info,
  Camera,
  Trash2,
  Loader2,
  MessageCircle,
  Lock,
  Sparkles
} from 'lucide-react'
import { toast } from 'sonner'
import api from '../../api/axios'
import {
  getProfile,
  updateProfile,
  uploadAvatar,
  deleteAvatar
} from '../../api/profile'
import { cancelSubscription } from '../../api/subscription'
import {
  optInWhatsApp,
  optOutWhatsApp,
  updateNotificationPreferences
} from '../../api/notifications'
import { Switch } from '../../components/ui/switch'
import { CurrencyInput } from '../../components/ui/currency-input'
import { useCurrency } from '../../contexts/Currency'
import { LanguageSwitcher } from '../../components/LanguageSwitcher'
import { UpgradeProModal } from '../../components/UpgradeProModal'
import avatarPlaceholder from '../../assets/avatar.png'
import CategoriaCustom from '../../components/CategoriaCustom'
import { PasswordInput } from '../../components/ui/password-input'

const normalizePhone = raw => (raw || '').replace(/[\s\-()]/g, '')

const E164_REGEX = /^\+[1-9]\d{7,14}$/

const DAILY_SUMMARY_HOUR = 21

const PENDING_DEBITS_HOUR = 9

function Profile() {
  const { user, setUser, storageUser } = useContext(AuthContext)
  const { t, i18n } = useTranslation()
  const language = i18n.language
  const {
    currency,
    setCurrency,
    currencies,
    format: formatCurrency
  } = useCurrency()

  const [isPasswordDialogOpen, setIsPasswordDialogOpen] = useState(false)
  const [isCancelDialogOpen, setIsCancelDialogOpen] = useState(false)
  const [isUpgradeOpen, setIsUpgradeOpen] = useState(false)
  const [cancelLoading, setCancelLoading] = useState(false)

  const [nome, setNome] = useState('')
  const [profissao, setProfissao] = useState('')
  const [idade, setIdade] = useState('')
  const [receita, setReceita] = useState('')
  const [email] = useState(user?.email || '')
  const [avatarURL, setAvatarURL] = useState('')
  const [incomeMode, setIncomeMode] = useState('fixed')
  const [whatsappPhone, setWhatsappPhone] = useState('')
  const [whatsappSaving, setWhatsappSaving] = useState(false)
  const [whatsappOptedIn, setWhatsappOptedIn] = useState(false)
  const [whatsappDailySummary, setWhatsappDailySummary] = useState(false)
  const [whatsappPendingDebits, setWhatsappPendingDebits] = useState(false)
  const [whatsappActionLoading, setWhatsappActionLoading] = useState(false)
  const [whatsappSummarySaving, setWhatsappSummarySaving] = useState(false)
  const [whatsappPendingSaving, setWhatsappPendingSaving] = useState(false)

  const isPro = user?.plano === 'pro'
  const planCancelAt = user?.planCancelAt ? new Date(user.planCancelAt) : null

  // Snapshot dos valores originais para o botão Cancelar restaurar o form
  const [originalSnapshot, setOriginalSnapshot] = useState(null)

  // Password fields
  const [currentPassword, setCurrentPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  // Avatar upload
  const avatarInputRef = useRef(null)
  const [avatarUploading, setAvatarUploading] = useState(false)

  const ALLOWED_AVATAR_TYPES = [
    'image/jpeg',
    'image/png',
    'image/webp',
    'image/gif'
  ]
  const MAX_AVATAR_SIZE_BYTES = 5 * 1024 * 1024
  const MIN_AVATAR_ZOOM = 0.6
  const MAX_AVATAR_ZOOM = 3

  const [cropModalOpen, setCropModalOpen] = useState(false)
  const [selectedImage, setSelectedImage] = useState(null)
  const [selectedImageFile, setSelectedImageFile] = useState(null)
  const [avatarEditSource, setAvatarEditSource] = useState(null)
  const [cropSessionKey, setCropSessionKey] = useState(0)
  const [crop, setCrop] = useState({ x: 0, y: 0 })
  const [zoom, setZoom] = useState(1)
  const [croppedAreaPixels, setCroppedAreaPixels] = useState(null)

  const resetCropState = () => {
    setCrop({ x: 0, y: 0 })
    setZoom(1)
    setCroppedAreaPixels(null)
  }

  const closeCropModal = () => {
    setCropModalOpen(false)
    setSelectedImage(null)
    setSelectedImageFile(null)
    resetCropState()
  }

  const beginCropSession = (imageSource, fileMeta) => {
    resetCropState()
    setSelectedImage(imageSource)
    setSelectedImageFile(fileMeta)
    setCropSessionKey(currentKey => currentKey + 1)
    setCropModalOpen(true)
  }

  const persistUserChanges = patch => {
    if (!user) return
    const updatedUser = { ...user, ...patch }
    setUser(updatedUser)
    const token =
      localStorage.getItem('token') || sessionStorage.getItem('token')
    const remember = !!localStorage.getItem('token')
    storageUser(updatedUser, token, remember)
  }

  const handleAvatarPick = () => {
    if (avatarUploading) return
    avatarInputRef.current?.click()
  }

  const openCropEditor = async file => {
    if (!file) return

    if (!ALLOWED_AVATAR_TYPES.includes(file.type)) {
      toast.error(t('invalidPhotoType'))
      return
    }

    if (file.size > MAX_AVATAR_SIZE_BYTES) {
      toast.error(t('photoTooLarge'))
      return
    }

    try {
      const imageDataUrl = await readFileAsDataUrl(file)

      beginCropSession(imageDataUrl, file)
    } catch (err) {
      console.error('Avatar image load failed', err)
      toast.error(t('photoUploadError'))
    }
  }

  const handleAvatarChange = async event => {
    const file = event.target.files?.[0]
    event.target.value = ''

    if (!file) return

    await openCropEditor(file)
  }

  const handleAvatarEdit = async () => {
    if (avatarUploading || !avatarURL) return

    if (avatarEditSource) {
      beginCropSession(avatarEditSource, buildAvatarFileMeta(avatarURL))
      return
    }

    try {
      setAvatarUploading(true)
      const editableSource = await fetchImageAsDataUrl(avatarURL)

      setAvatarEditSource(editableSource)
      await persistAvatarEditSource(user?.id, editableSource)
      beginCropSession(editableSource, buildAvatarFileMeta(avatarURL))
    } catch (err) {
      console.error('Avatar edit load failed', err)
      beginCropSession(avatarURL, buildAvatarFileMeta(avatarURL))
    } finally {
      setAvatarUploading(false)
    }
  }

  const handleCropSave = async () => {
    try {
      if (
        !croppedAreaPixels ||
        !selectedImage ||
        !selectedImageFile ||
        !user?.id
      )
        return

      setAvatarUploading(true)

      const croppedFile = await getCroppedImg(
        selectedImage,
        croppedAreaPixels,
        {
          fileName: selectedImageFile.name,
          mimeType: selectedImageFile.type
        }
      )
      const nextAvatarEditSource = await readFileAsDataUrl(croppedFile)

      const { avatarUrl } = await uploadAvatar(user.id, croppedFile)

      setAvatarURL(avatarUrl || '')

      persistUserChanges({
        avatarUrl: avatarUrl || null
      })

      setOriginalSnapshot(prev =>
        prev ? { ...prev, avatarUrl: avatarUrl || '' } : prev
      )
      setAvatarEditSource(nextAvatarEditSource)
      await persistAvatarEditSource(user.id, nextAvatarEditSource)

      toast.success(t('photoUpdated'))

      closeCropModal()
    } catch (err) {
      console.error('Avatar upload failed', {
        error: err,
        response: err?.response?.data
      })

      const status = err?.response?.status

      if (status === 413) toast.error(t('photoTooLarge'))
      else if (status === 415) toast.error(t('invalidPhotoType'))
      else toast.error(err?.response?.data?.error || t('photoUploadError'))
    } finally {
      setAvatarUploading(false)
    }
  }

  const handleAvatarRemove = async () => {
    if (avatarUploading || !user?.id || !avatarURL) return
    try {
      setAvatarUploading(true)
      await deleteAvatar(user.id)
      setAvatarURL('')
      setAvatarEditSource(null)
      await clearAvatarEditSource(user.id)
      persistUserChanges({ avatarUrl: null })
      setOriginalSnapshot(prev => (prev ? { ...prev, avatarUrl: '' } : prev))
      toast.success(t('photoRemoved'))
    } catch (err) {
      console.error(err)
      toast.error(t('photoRemoveError'))
    } finally {
      setAvatarUploading(false)
    }
  }

  useEffect(() => {
    if (!user?.id) return

    getProfile(user.id)
      .then(async u => {
        setNome(u.nome)
        setProfissao(u.profissao || '')
        setIdade(u.idade || '')
        setReceita(u.receita || '')
        setAvatarURL(u.avatarUrl || '')
        setAvatarEditSource(await getPersistedAvatarEditSource(user.id))
        setIncomeMode(u.incomeMode || 'fixed')
        setWhatsappPhone(u.whatsappPhone || '')
        setWhatsappOptedIn(!!u.whatsappOptedIn)
        setWhatsappDailySummary(u.whatsappDailySummary ?? false)
        setWhatsappPendingDebits(u.whatsappPendingDebits ?? false)
        if (u.currency) setCurrency(u.currency)
        // sincroniza dados de plano no AuthContext (caso webhook tenha atualizado)
        if (u.plano !== user.plano || u.planCancelAt !== user.planCancelAt) {
          persistUserChanges({
            plano: u.plano,
            planStatus: u.planStatus,
            planId: u.planId,
            planCancelAt: u.planCancelAt
          })
        }
        setOriginalSnapshot({
          nome: u.nome,
          profissao: u.profissao || '',
          idade: u.idade || '',
          receita: u.receita || '',
          avatarUrl: u.avatarUrl || '',
          incomeMode: u.incomeMode || 'fixed',
          currency: u.currency || currency
        })
      })
      .catch(() => toast.error(t('errorLoadingProfile')))
  }, [user?.id, language, t])

  const handleSaveWhatsapp = async () => {
    if (!user?.id) return
    const trimmed = normalizePhone(whatsappPhone)
    if (trimmed && !E164_REGEX.test(trimmed)) {
      toast.error(t('whatsappInvalidNumber'))
      return
    }
    setWhatsappPhone(trimmed)
    try {
      setWhatsappSaving(true)
      await updateProfile(user.id, { whatsappPhone: trimmed || null })
      persistUserChanges({ whatsappPhone: trimmed || null })
      toast.success(t('whatsappSaved'))
    } catch (err) {
      console.error(err)
      toast.error(t('profileUpdateError'))
    } finally {
      setWhatsappSaving(false)
    }
  }

  const handleOptInWhatsapp = async () => {
    const trimmed = normalizePhone(whatsappPhone)
    if (!E164_REGEX.test(trimmed)) {
      toast.error(t('whatsappInvalidNumber'))
      return
    }
    setWhatsappPhone(trimmed)

    try {
      setWhatsappActionLoading(true)
      const result = await optInWhatsApp(trimmed)
      setWhatsappPhone(result.whatsappPhone || trimmed)
      setWhatsappOptedIn(!!result.whatsappOptedIn)
      setWhatsappDailySummary(result.whatsappDailySummary ?? false)
      setWhatsappPendingDebits(result.whatsappPendingDebits ?? false)
      persistUserChanges({
        whatsappPhone: result.whatsappPhone || trimmed,
        whatsappOptedIn: !!result.whatsappOptedIn,
        whatsappDailySummary: result.whatsappDailySummary ?? false,
        whatsappPendingDebits: result.whatsappPendingDebits ?? false
      })
      toast.success(t('whatsappNotificationsEnabled'))
    } catch (err) {
      console.error(err)
      toast.error(err?.response?.data?.error || t('whatsappEnableError'))
    } finally {
      setWhatsappActionLoading(false)
    }
  }

  const handleOptOutWhatsapp = async () => {
    try {
      setWhatsappActionLoading(true)
      const result = await optOutWhatsApp()
      setWhatsappOptedIn(!!result.whatsappOptedIn)
      persistUserChanges({ whatsappOptedIn: !!result.whatsappOptedIn })
      toast.success(t('whatsappNotificationsDisabled'))
    } catch (err) {
      console.error(err)
      toast.error(err?.response?.data?.error || t('whatsappDisableError'))
    } finally {
      setWhatsappActionLoading(false)
    }
  }

  const handleDailySummaryToggle = async checked => {
    if (whatsappSummarySaving) return
    const previous = whatsappDailySummary
    setWhatsappDailySummary(checked)
    try {
      setWhatsappSummarySaving(true)
      await updateNotificationPreferences({ whatsappDailySummary: checked })
      persistUserChanges({ whatsappDailySummary: checked })
    } catch (err) {
      console.error(err)
      setWhatsappDailySummary(previous)
      toast.error(err?.response?.data?.error || t('whatsappPreferenceUpdateError'))
    } finally {
      setWhatsappSummarySaving(false)
    }
  }

  const handlePendingDebitsToggle = async checked => {
    if (whatsappPendingSaving) return
    const previous = whatsappPendingDebits
    setWhatsappPendingDebits(checked)
    try {
      setWhatsappPendingSaving(true)
      await updateNotificationPreferences({ whatsappPendingDebits: checked })
      persistUserChanges({ whatsappPendingDebits: checked })
    } catch (err) {
      console.error(err)
      setWhatsappPendingDebits(previous)
      toast.error(err?.response?.data?.error || t('whatsappPreferenceUpdateError'))
    } finally {
      setWhatsappPendingSaving(false)
    }
  }

  const handleCancelPlan = async () => {
    try {
      setCancelLoading(true)
      const { planCancelAt: at } = await cancelSubscription()
      persistUserChanges({ planCancelAt: at, planStatus: 'canceled' })
      setIsCancelDialogOpen(false)
      toast.success(t('planCanceledScheduledToast'))
    } catch (err) {
      console.error(err)
      toast.error(err?.response?.data?.error || t('paymentUnexpectedError'))
    } finally {
      setCancelLoading(false)
    }
  }

  const formatPlanCancelDate = () => {
    if (!planCancelAt) return ''
    return planCancelAt.toLocaleDateString(i18n.language || 'pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    })
  }

  const isDirty = useMemo(() => {
    if (!originalSnapshot) return false
    return (
      String(nome) !== String(originalSnapshot.nome || '') ||
      String(profissao) !== String(originalSnapshot.profissao || '') ||
      String(idade) !== String(originalSnapshot.idade || '') ||
      String(receita) !== String(originalSnapshot.receita || '') ||
      String(avatarURL) !== String(originalSnapshot.avatarUrl || '') ||
      incomeMode !== (originalSnapshot.incomeMode || 'fixed') ||
      currency !== originalSnapshot.currency
    )
  }, [
    nome,
    profissao,
    idade,
    receita,
    avatarURL,
    incomeMode,
    currency,
    originalSnapshot
  ])

  const handleSave = async () => {
    try {
      const idadeNum = parseInt(idade, 10)
      if (
        idade !== '' &&
        idade != null &&
        (Number.isNaN(idadeNum) || idadeNum < 0 || idadeNum > 130)
      ) {
        toast.error(t('invalidAge'))
        return
      }

      const receitaNum = parseFloat(receita)
      if (
        receita !== '' &&
        receita != null &&
        (Number.isNaN(receitaNum) || receitaNum < 0)
      ) {
        toast.error(t('invalidIncome'))
        return
      }

      const data = {
        nome,
        profissao,
        idade: Number.isFinite(idadeNum) ? idadeNum : 0,
        receita: Number.isFinite(receitaNum) ? receitaNum : 0,
        avatarUrl: avatarURL,
        incomeMode,
        currency
      }

      await updateProfile(user.id, data)

      // Update context
      const updatedUser = { ...user, ...data }
      setUser(updatedUser)
      const token =
        localStorage.getItem('token') || sessionStorage.getItem('token')
      const remember = !!localStorage.getItem('token')
      storageUser(updatedUser, token, remember)

      setOriginalSnapshot({
        nome: data.nome,
        profissao: data.profissao,
        idade: data.idade,
        receita: data.receita,
        avatarUrl: data.avatarUrl,
        incomeMode: data.incomeMode,
        currency: data.currency
      })
      toast.success(t('profileUpdatedSuccess'))
    } catch (error) {
      toast.error(t('profileUpdateError'))
    }
  }

  const handleCancelEdit = () => {
    if (!originalSnapshot) return
    setNome(originalSnapshot.nome || '')
    setProfissao(originalSnapshot.profissao || '')
    setIdade(originalSnapshot.idade || '')
    setReceita(originalSnapshot.receita || '')
    setAvatarURL(originalSnapshot.avatarUrl || '')
    setIncomeMode(originalSnapshot.incomeMode || 'fixed')
    if (originalSnapshot.currency) setCurrency(originalSnapshot.currency)
  }

  const handlePasswordChange = async () => {
    if (!currentPassword || !newPassword || !confirmPassword) {
      toast.error(t('fillAllFields'))
      return
    }

    if (newPassword !== confirmPassword) {
      toast.error(t('passwordsDoNotMatch'))
      return
    }

    if (newPassword.length < 6) {
      toast.error(t('passwordMin6'))
      return
    }

    try {
      await changePassword(currentPassword, newPassword)
      setIsPasswordDialogOpen(false)
      setCurrentPassword('')
      setNewPassword('')
      setConfirmPassword('')
      toast.success(t('passwordChangedSuccess'))
    } catch (err) {
      const errorMessage = err.response?.data?.error || t('passwordChangeError')

      toast.error(errorMessage)
    }
  }

  const getInitials = name => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2)
  }

  const applyAvatarZoom = nextZoomValue => {
    const nextZoom = Math.min(
      MAX_AVATAR_ZOOM,
      Math.max(MIN_AVATAR_ZOOM, Number(nextZoomValue.toFixed(1)))
    )

    setZoom(nextZoom)

    if (nextZoom <= 1) {
      setCrop({ x: 0, y: 0 })
    }
  }

  const handleZoomOut = () => {
    applyAvatarZoom(zoom - 0.2)
  }

  const handleZoomIn = () => {
    applyAvatarZoom(zoom + 0.2)
  }

  const handleAvatarCropChange = nextCrop => {
    if (zoom <= 1) {
      setCrop({ x: 0, y: 0 })
      return
    }

    setCrop(nextCrop)
  }

  return (
    <AppLayout
      title={t('profile')}
      subtitle={t('managePersonalInfo')}
      icon={User}
    >
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Profile Card */}
        <Card className="p-6 lg:col-span-1">
          <div className="flex flex-col items-center text-center space-y-4">
            <input
              ref={avatarInputRef}
              type="file"
              accept={ALLOWED_AVATAR_TYPES.join(',')}
              className="hidden"
              onChange={handleAvatarChange}
            />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  type="button"
                  disabled={avatarUploading}
                  className="relative group rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:cursor-not-allowed"
                  aria-label={t('changePhoto')}
                >
                  <Avatar className="w-32 h-32">
                    <AvatarImage src={avatarURL || avatarPlaceholder} />
                    <AvatarFallback className="text-2xl bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
                      {getInitials(nome || user?.nome || 'User')}
                    </AvatarFallback>
                  </Avatar>
                  <span className="absolute inset-0 flex items-center justify-center rounded-full bg-black/45 text-white opacity-0 transition group-hover:opacity-100 group-focus-within:opacity-100 data-[state=open]:opacity-100">
                    {avatarUploading ? (
                      <Loader2 className="w-6 h-6 animate-spin" />
                    ) : (
                      <Camera className="w-6 h-6" />
                    )}
                  </span>
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" sideOffset={8}>
                <DropdownMenuItem
                  onSelect={handleAvatarPick}
                  disabled={avatarUploading}
                >
                  <Camera className="w-4 h-4" />
                  {avatarURL ? t('changePhoto') : t('addPhoto')}
                </DropdownMenuItem>
                {avatarURL && (
                  <DropdownMenuItem
                    onSelect={handleAvatarEdit}
                    disabled={avatarUploading}
                  >
                    <Camera className="w-4 h-4" />
                    {t('editPhoto')}
                  </DropdownMenuItem>
                )}
                {avatarURL && (
                  <DropdownMenuItem
                    onSelect={handleAvatarRemove}
                    disabled={avatarUploading}
                    className="text-red-600 focus:text-red-700 focus:bg-red-50"
                  >
                    <Trash2 className="w-4 h-4" />
                    {t('removePhoto')}
                  </DropdownMenuItem>
                )}
              </DropdownMenuContent>
            </DropdownMenu>

            <div>
              <h2 className="text-slate-900 font-bold text-xl mb-1">
                {nome || user?.nome}
              </h2>
              <p className="text-slate-600">
                {profissao || t('occupationNotProvided')}
              </p>
            </div>

            <div className="w-full pt-4 border-t space-y-3">
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">{t('age')}</span>
                <span className="text-slate-900 font-semibold">
                  {idade ? `${idade} ${t('years')}` : '-'}
                </span>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">{t('monthlyIncome')}</span>
                <span className="text-slate-900 font-semibold">
                  {receita ? formatCurrency(parseFloat(receita)) : '-'}
                </span>
              </div>

              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">{t('password')}</span>

                <Dialog
                  open={isPasswordDialogOpen}
                  onOpenChange={setIsPasswordDialogOpen}
                >
                  <DialogTrigger asChild>
                    <Button
                      variant="link"
                      className="h-auto p-0 text-blue-600 hover:text-blue-700 font-semibold"
                    >
                      {t('changePassword')}
                    </Button>
                  </DialogTrigger>

                  <DialogContent>
                    <DialogHeader>
                      <DialogTitle>{t('changePassword')}</DialogTitle>
                    </DialogHeader>

                    <div className="space-y-4">
                      <div className="space-y-2">
                        <Label>{t('currentPassword')}</Label>
                        <PasswordInput
                          id="currentPassword"
                          name="currentPassword"
                          value={currentPassword}
                          onChange={e => setCurrentPassword(e.target.value)}
                          autoComplete="current-password"
                          ariaLabelShow={t('showPassword')}
                          ariaLabelHide={t('hidePassword')}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label>{t('newPassword')}</Label>
                        <PasswordInput
                          id="newPassword"
                          name="newPassword"
                          value={newPassword}
                          onChange={e => setNewPassword(e.target.value)}
                          autoComplete="new-password"
                          ariaLabelShow={t('showPassword')}
                          ariaLabelHide={t('hidePassword')}
                        />
                      </div>

                      <div className="space-y-2">
                        <Label>{t('confirmNewPassword')}</Label>
                        <PasswordInput
                          id="confirmPassword"
                          name="confirmNewPassword"
                          value={confirmPassword}
                          onChange={e => setConfirmPassword(e.target.value)}
                          autoComplete="new-password"
                          ariaLabelShow={t('showPassword')}
                          ariaLabelHide={t('hidePassword')}
                        />
                      </div>
                    </div>
                    <div className="flex justify-end gap-2 pt-2">
                      <Button
                        variant="outline"
                        onClick={() => setIsPasswordDialogOpen(false)}
                      >
                        {t('cancel')}
                      </Button>

                      <Button onClick={handlePasswordChange}>
                        {t('save')}
                      </Button>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
              <div className="flex items-center justify-between text-sm">
                <span className="text-slate-600">{t('currentPlan')}</span>
                {isPro ? (
                  planCancelAt ? (
                    <span className="text-slate-900 font-semibold">
                      {t('proUntil', { date: formatPlanCancelDate() })}
                    </span>
                  ) : (
                    <Dialog
                      open={isCancelDialogOpen}
                      onOpenChange={setIsCancelDialogOpen}
                    >
                      <DialogTrigger asChild>
                        <Button
                          variant="link"
                          className="h-auto p-0 text-red-600 hover:text-red-700 font-semibold"
                        >
                          {t('cancelPlan')}
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>
                            {t('cancelPlanConfirmTitle')}
                          </DialogTitle>
                        </DialogHeader>
                        <p className="text-sm text-slate-600">
                          {t('cancelPlanConfirmDescription')}
                        </p>
                        <div className="flex justify-end gap-2 pt-2">
                          <Button
                            variant="outline"
                            onClick={() => setIsCancelDialogOpen(false)}
                            disabled={cancelLoading}
                          >
                            {t('keepPlan')}
                          </Button>
                          <Button
                            onClick={handleCancelPlan}
                            disabled={cancelLoading}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            {cancelLoading ? (
                              <Loader2 className="w-4 h-4 animate-spin" />
                            ) : (
                              t('confirmCancel')
                            )}
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  )
                ) : (
                  <Button
                    variant="link"
                    onClick={() => setIsUpgradeOpen(true)}
                    className="h-auto p-0 text-indigo-600 hover:text-indigo-700 font-semibold"
                  >
                    <Sparkles className="w-3.5 h-3.5 mr-1" />
                    {t('upgradeToPro')}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </Card>

        {/* Information Card */}
        <Card className="p-6 lg:col-span-2">
          <div className="flex items-center justify-between mb-6">
            <h3 className="text-slate-900 font-semibold text-lg">
              {t('personalInfo')}
            </h3>
            {isDirty && (
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleCancelEdit}>
                  {t('cancel')}
                </Button>
                <Button onClick={handleSave}>
                  <Save className="w-4 h-4 mr-2" />
                  {t('save')}
                </Button>
              </div>
            )}
          </div>

          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="space-y-2">
                <Label>{t('fullName')}</Label>
                <div className="flex items-center gap-2">
                  <User className="w-4 h-4 text-slate-400" />
                  <Input value={nome} onChange={e => setNome(e.target.value)} />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Email</Label>
                <div className="flex items-center gap-2">
                  <Mail className="w-4 h-4 text-slate-400" />
                  <Input
                    type="email"
                    value={email}
                    disabled
                    className="bg-slate-50"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('profession')}</Label>
                <div className="flex items-center gap-2">
                  <Briefcase className="w-4 h-4 text-slate-400" />
                  <Input
                    value={profissao}
                    onChange={e => setProfissao(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>{t('age')}</Label>
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4 text-slate-400" />
                  <Input
                    type="number"
                    min="0"
                    max="130"
                    step="1"
                    value={idade}
                    onChange={e => setIdade(e.target.value)}
                  />
                </div>
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label>{t('incomeMode')}</Label>
                <Select value={incomeMode} onValueChange={setIncomeMode}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="fixed">{t('fixedIncome')}</SelectItem>
                    <SelectItem value="variable">
                      {t('variableIncome')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div
                className={`space-y-2 ${incomeMode === 'fixed' ? '' : 'md:col-span-2'}`}
              >
                <Label>{t('displayCurrency')}</Label>
                <Select value={currency} onValueChange={setCurrency}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {currencies.map(c => (
                      <SelectItem key={c.code} value={c.code}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <p className="text-xs text-slate-500">
                  {t('displayCurrencyHelp')}
                </p>
              </div>

              <div className="space-y-2 md:col-span-2">
                <Label>{t('language')}</Label>
                <LanguageSwitcher
                  onChange={async lng => {
                    try {
                      if (user?.id) {
                        const updated = await updateProfile(user.id, {
                          language: lng
                        })
                        const merged = { ...user, ...updated }
                        setUser(merged)
                        const token =
                          localStorage.getItem('token') ||
                          sessionStorage.getItem('token')
                        const remember = !!localStorage.getItem('token')
                        storageUser(merged, token, remember)
                      }
                    } catch (err) {
                      console.error('falha salvando idioma', err)
                    }
                  }}
                />
                <p className="text-xs text-slate-500">{t('languageHelp')}</p>
              </div>

              {incomeMode === 'fixed' ? (
                <div className="space-y-2">
                  <Label>{t('netMonthlyIncome')}</Label>
                  <CurrencyInput
                    value={receita}
                    onValueChange={val => setReceita(val)}
                    placeholder={t('currencyPlaceholder')}
                  />
                </div>
              ) : (
                <div className="md:col-span-2 flex items-start gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
                  <Info className="w-5 h-5 text-blue-600 shrink-0 mt-0.5" />
                  <p className="text-sm text-blue-800">
                    {t('variableIncomeInfo')}
                  </p>
                </div>
              )}
            </div>
          </div>
        </Card>

        <Card
          className={`p-6 lg:col-span-3 ${isPro ? '' : 'bg-gradient-to-br from-indigo-50 to-blue-50 border-indigo-200'}`}
        >
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-start gap-3">
              <div
                className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full ${isPro ? 'bg-emerald-100 text-emerald-600' : 'bg-indigo-100 text-indigo-600'}`}
              >
                {isPro ? (
                  <MessageCircle className="w-5 h-5" />
                ) : (
                  <Lock className="w-5 h-5" />
                )}
              </div>
              <div>
                <h3 className="text-slate-900 font-semibold text-lg">
                  {t('connectWithIsy')}
                </h3>
                <p className="text-sm text-slate-600 mt-1">
                  {isPro
                    ? t('connectWithIsyDescription')
                    : t('connectWithIsyTeaser')}
                </p>
              </div>
            </div>

            {!isPro && (
              <Button
                onClick={() => setIsUpgradeOpen(true)}
                className="bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 shrink-0"
              >
                <Sparkles className="w-4 h-4 mr-2" />
                {t('upgradeToPro')}
              </Button>
            )}
          </div>

          {isPro && (
            <div className="mt-6 space-y-4">
              <div className="grid grid-cols-1 gap-3 md:grid-cols-[1fr_auto] md:items-end">
                <div className="space-y-2">
                  <Label>{t('whatsappNumber')}</Label>
                  <Input
                    type="tel"
                    placeholder={t('whatsappPlaceholder')}
                    value={whatsappPhone}
                    onChange={e =>
                      setWhatsappPhone(
                        e.target.value.replace(/[^\d+()\s-]/g, '')
                      )
                    }
                    disabled={whatsappOptedIn}
                  />
                  {whatsappOptedIn ? (
                    <p className="text-xs text-slate-500">
                      {t('whatsappNumberLocked')}
                    </p>
                  ) : whatsappPhone &&
                    !E164_REGEX.test(normalizePhone(whatsappPhone)) ? (
                    <p className="text-xs text-red-600">
                      {t('whatsappInvalidNumber')}
                    </p>
                  ) : null}
                </div>
                <Button
                  onClick={handleSaveWhatsapp}
                  disabled={
                    whatsappSaving ||
                    whatsappOptedIn ||
                    whatsappPhone === (user?.whatsappPhone || '')
                  }
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {whatsappSaving ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <>
                      <MessageCircle className="w-4 h-4 mr-2" />
                      {user?.whatsappPhone ? t('updateNumber') : t('connect')}
                    </>
                  )}
                </Button>
              </div>

              {user?.whatsappPhone && (
                <div className="flex flex-col gap-3 rounded-lg border border-slate-200 bg-slate-50 p-4 md:flex-row md:items-center md:justify-between">
                  <div className="flex items-center gap-3">
                    <span
                      className={`inline-flex h-2.5 w-2.5 rounded-full ${
                        whatsappOptedIn ? 'bg-emerald-500' : 'bg-slate-400'
                      }`}
                    />
                    <div>
                      <p className="text-sm font-medium text-slate-900">
                        {whatsappOptedIn
                          ? t('whatsappStatusActive')
                          : t('whatsappStatusInactive')}
                      </p>
                      <p className="text-xs text-slate-500">
                        {whatsappOptedIn
                          ? t('whatsappActiveHelp')
                          : t('whatsappInactiveHelp')}
                      </p>
                    </div>
                  </div>

                  {whatsappOptedIn ? (
                    <Button
                      variant="outline"
                      onClick={handleOptOutWhatsapp}
                      disabled={whatsappActionLoading}
                    >
                      {whatsappActionLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        t('whatsappDisable')
                      )}
                    </Button>
                  ) : (
                    <Button
                      onClick={handleOptInWhatsapp}
                      disabled={whatsappActionLoading || !whatsappPhone}
                      className="bg-emerald-600 hover:bg-emerald-700"
                    >
                      {whatsappActionLoading ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        t('whatsappEnableNotifications')
                      )}
                    </Button>
                  )}
                </div>
              )}

              {whatsappOptedIn && (
                <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-4">
                  <div>
                    <p
                      id="whatsapp-daily-summary-label"
                      className="text-sm font-medium text-slate-900"
                    >
                      {t('whatsappDailySummaryTitle')}
                    </p>
                    <p className="text-xs text-slate-500">
                      {t('whatsappDailySummaryDescription', {
                        hour: DAILY_SUMMARY_HOUR
                      })}
                    </p>
                  </div>
                  <Switch
                    id="whatsapp-daily-summary"
                    aria-labelledby="whatsapp-daily-summary-label"
                    checked={whatsappDailySummary}
                    onCheckedChange={handleDailySummaryToggle}
                    disabled={whatsappSummarySaving}
                  />
                </div>
              )}

              {whatsappOptedIn && (
                <div className="flex items-center justify-between rounded-lg border border-slate-200 bg-white p-4">
                  <div>
                    <p
                      id="whatsapp-pending-debits-label"
                      className="text-sm font-medium text-slate-900"
                    >
                      {t('whatsappPendingDebitsTitle')}
                    </p>
                    <p className="text-xs text-slate-500">
                      {t('whatsappPendingDebitsDescription', {
                        hour: PENDING_DEBITS_HOUR
                      })}
                    </p>
                  </div>
                  <Switch
                    id="whatsapp-pending-debits"
                    aria-labelledby="whatsapp-pending-debits-label"
                    checked={whatsappPendingDebits}
                    onCheckedChange={handlePendingDebitsToggle}
                    disabled={whatsappPendingSaving}
                  />
                </div>
              )}
            </div>
          )}
        </Card>

        <Card className="p-6 lg:col-span-3">
          <div className="mb-6">
            <h3 className="text-slate-900 font-semibold text-lg">
              {t('customizations')}
            </h3>
            <p className="text-sm text-slate-500 mt-1">
              {t('customizationsDescription')}
            </p>
          </div>

          <CategoriaCustom userId={user?._id || user?.id} />
        </Card>
      </div>
      <Dialog
        open={cropModalOpen}
        onOpenChange={open => {
          if (open) {
            setCropModalOpen(true)
            return
          }

          closeCropModal()
        }}
      >
        <DialogContent className="max-w-lg overflow-visible data-[state=open]:animate-none data-[state=closed]:animate-none sm:max-w-xl">
          <DialogHeader>
            <DialogTitle>{t('editPhoto')}</DialogTitle>
          </DialogHeader>

          <div className="relative h-[400px] w-full overflow-hidden rounded-lg bg-slate-950">
            {selectedImage && (
              <Cropper
                key={`${cropSessionKey}-${selectedImage}`}
                image={selectedImage}
                crop={crop}
                zoom={zoom}
                aspect={1}
                cropShape="round"
                objectFit="cover"
                showGrid={false}
                minZoom={MIN_AVATAR_ZOOM}
                maxZoom={MAX_AVATAR_ZOOM}
                restrictPosition
                onCropChange={handleAvatarCropChange}
                onZoomChange={applyAvatarZoom}
                onCropComplete={(_, croppedAreaPixels) => {
                  setCroppedAreaPixels(croppedAreaPixels)
                }}
                onMediaLoaded={() => {
                  applyAvatarZoom(1)
                }}
                style={{
                  containerStyle: {
                    width: '100%',
                    height: '100%',
                    backgroundColor: '#020617'
                  },
                  mediaStyle: {
                    display: 'block'
                  },
                  cropAreaStyle: {
                    borderRadius: '50%',
                    border: '2px solid rgba(255, 255, 255, 0.9)'
                  }
                }}
              />
            )}
          </div>

          <div className="flex items-center justify-between pt-2">
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={handleZoomOut}>
                -
              </Button>
              <Button variant="outline" size="sm" onClick={handleZoomIn}>
                +
              </Button>
            </div>

            <span className="w-14 text-right text-sm text-slate-500">
              {zoom.toFixed(1)}x
            </span>
          </div>

          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={closeCropModal}>
              {t('cancel')}
            </Button>

            <Button onClick={handleCropSave} disabled={!croppedAreaPixels}>
              {t('save')}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
      <UpgradeProModal open={isUpgradeOpen} onOpenChange={setIsUpgradeOpen} />
    </AppLayout>
  )
}

export default Profile

function readFileAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(reader.result)
    reader.onerror = () =>
      reject(reader.error || new Error('Failed to read file'))
    reader.readAsDataURL(file)
  })
}

function buildAvatarFileMeta(imageUrl) {
  const cleanUrl = imageUrl.split('?')[0].toLowerCase()
  const mimeTypeByExtension = {
    '.jpg': 'image/jpeg',
    '.jpeg': 'image/jpeg',
    '.png': 'image/png',
    '.webp': 'image/webp',
    '.gif': 'image/gif'
  }

  const matchedExtension = Object.keys(mimeTypeByExtension).find(extension =>
    cleanUrl.endsWith(extension)
  )
  const mimeType = matchedExtension
    ? mimeTypeByExtension[matchedExtension]
    : 'image/jpeg'
  const extension = matchedExtension ? matchedExtension.slice(1) : 'jpg'

  return {
    name: `avatar-edit.${extension}`,
    type: mimeType
  }
}

async function fetchImageAsDataUrl(imageUrl) {
  const token =
    localStorage.getItem('token') || sessionStorage.getItem('token')
  const response = await api.get(imageUrl, {
    responseType: 'blob',
    headers: token ? { Authorization: `Bearer ${token}` } : undefined
  })

  if (!response?.data) {
    throw new Error('Failed to fetch image')
  }

  const blob = response.data
  return readFileAsDataUrl(
    new File([blob], buildAvatarFileMeta(imageUrl).name, {
      type: blob.type || buildAvatarFileMeta(imageUrl).type
    })
  )
}

async function persistAvatarEditSource(userId, source) {
  if (!userId || !source) return

  const db = await openAvatarEditSourceDb()
  await runAvatarStoreRequest(db, 'readwrite', store => {
    store.put({ userId, source, updatedAt: Date.now() })
  })
}

async function getPersistedAvatarEditSource(userId) {
  if (!userId) return null

  try {
    const db = await openAvatarEditSourceDb()
    const record = await runAvatarStoreRequest(db, 'readonly', store =>
      store.get(userId)
    )

    return record?.source || null
  } catch {
    return null
  }
}

async function clearAvatarEditSource(userId) {
  if (!userId) return

  const db = await openAvatarEditSourceDb()
  await runAvatarStoreRequest(db, 'readwrite', store => {
    store.delete(userId)
  })
}

function openAvatarEditSourceDb() {
  return new Promise((resolve, reject) => {
    const request = window.indexedDB.open('avatar-edit-source-db', 1)

    request.onerror = () =>
      reject(request.error || new Error('Failed to open avatar source db'))

    request.onupgradeneeded = () => {
      const db = request.result

      if (!db.objectStoreNames.contains('sources')) {
        db.createObjectStore('sources', { keyPath: 'userId' })
      }
    }

    request.onsuccess = () => resolve(request.result)
  })
}

function runAvatarStoreRequest(db, mode, executor) {
  return new Promise((resolve, reject) => {
    const transaction = db.transaction('sources', mode)
    const store = transaction.objectStore('sources')
    const request = executor(store)

    if (request) {
      request.onerror = () =>
        reject(request.error || new Error('Avatar source request failed'))
      request.onsuccess = () => resolve(request.result)
      return
    }

    transaction.onerror = () =>
      reject(transaction.error || new Error('Avatar source transaction failed'))
    transaction.oncomplete = () => resolve(null)
  })
}
